// client/map/core/MapEngine.ts
import type { MapConfig } from "./MapConfig";
import type { SkopaTarget, SkopaPlatform } from "../types";
import type { AircraftUpdater } from "../updaters/AircraftUpdater";
import type { SilosUpdater } from "../updaters/SilosUpdater";
import maplibregl, { type Map as MapType } from "maplibre-gl";
import { sources } from "../sources";
import { layers } from "../layers";
import { UpdaterRegistry } from "../updaters/UpdaterRegistry";
import { MapEventBus } from "./MapEventBus";
import { InteractionManager } from "../interactions/InteractionsManager";
import { AircraftManager } from "../managers/aircraft/AircraftManager";
import { SilosManager } from "../managers/silos/SilosManager";
import { PdarsManager } from "../managers/pdars/PdarsManager";
import { PrimitivesManager } from "../managers/primitives/PrimitivesManager";
import { HeatmapManager } from "../managers/heatmap/HeatmapManager";
import { PdarProbabilityFieldManager } from "../managers/pdarProbabilityField/PdarProbabilityFieldManager";
import type { PdarsUpdater } from "../updaters/PdarsUpdater";
import type { PrimitivesUpdater } from "../updaters/PrimitivesUpdater";
import type { HeatmapUpdater } from "../updaters/HeatmapUpdater";
import type { PdarProbabilityFieldUpdater } from "../updaters/PdarProbabilityFieldUpdater";
import { registerAircraftSprites } from "../markers/registerAircraftSprites";
import { registerSiloSprites } from "../markers/registerSiloSprites";
import { registerPdarSprites } from "../markers/registerPdarSprites";
import { LAYERS } from "../layers/constants";
import { SOURCES } from "../sources";

export class MapEngine {
  private static instance: MapEngine | null = null;
  private map: MapType | null = null;
  private eventBus: MapEventBus;
  private updaterRegistry: UpdaterRegistry | null = null;
  private interactionManager: InteractionManager | null = null;
  private aircraftManager: AircraftManager | null = null;
  private silosManager: SilosManager | null = null;
  private pdarsManager: PdarsManager | null = null;
  private primitivesManager: PrimitivesManager | null = null;
  private heatmapManager: HeatmapManager | null = null;
  private pdarProbabilityFieldManager: PdarProbabilityFieldManager | null = null;

  private constructor() {
    this.eventBus = new MapEventBus();
  }

  static getInstance(): MapEngine {
    if (!MapEngine.instance) {
      MapEngine.instance = new MapEngine();
    }
    return MapEngine.instance;
  }

  initialize(container: HTMLElement, config: MapConfig): MapType {
    if (this.map) {
      console.warn("Map already initialised");
      return this.map;
    }

    this.map = new maplibregl.Map({
      container,
      ...config,
    });

    this.map.on("load", async () => {
      if (!this.map) return;

      this.map.setProjection({ type: "globe" });

      await Promise.all([
        registerAircraftSprites(this.map),
        registerSiloSprites(this.map),
        registerPdarSprites(this.map),
      ]);

      await this.onMapLoad();
    });

    return this.map;
  }

  async handleWebSocketMessage(topic: string, payload: any) {
    if (this.updaterRegistry) {
      await this.updaterRegistry.handleMessage(topic, payload);
    }
  }

  private async onMapLoad() {
    if (!this.map) return;

    Object.entries(sources).forEach(([id, spec]) => {
      this.map!.addSource(id, spec);
    });

    layers.forEach((layer) => {
      this.map!.addLayer(layer);
    });

    this.silosManager = new SilosManager(this.map, this.eventBus);
    this.pdarsManager = new PdarsManager(this.map, this.eventBus);
    this.aircraftManager = new AircraftManager(this.map, this.eventBus);
    // All primitive layers (fill + the dash-style line layers) read from this
    // single source; the manager just writes feature collections into it.
    this.primitivesManager = new PrimitivesManager(this.map, this.eventBus, {
      sourceId: SOURCES.PRIMITIVES,
    });
    this.heatmapManager = new HeatmapManager(this.map, this.eventBus);
    this.pdarProbabilityFieldManager = new PdarProbabilityFieldManager(
      this.map,
      this.eventBus,
    );

    this.updaterRegistry = new UpdaterRegistry(this.map);
    await this.updaterRegistry.initialise();

    const aircraftUpdater =
      this.updaterRegistry.getUpdater<AircraftUpdater>("aircraft");
    const silosUpdater = this.updaterRegistry.getUpdater<SilosUpdater>("silos");
    const pdarsUpdater = this.updaterRegistry.getUpdater<PdarsUpdater>("pdars");
    const primitivesUpdater =
      this.updaterRegistry.getUpdater<PrimitivesUpdater>("primitives");
    const heatmapUpdater =
      this.updaterRegistry.getUpdater<HeatmapUpdater>("heatmap");
    const pdarProbabilityFieldUpdater =
      this.updaterRegistry.getUpdater<PdarProbabilityFieldUpdater>(
        "pdarProbabilityField",
      );

    if (silosUpdater) silosUpdater.setSilosManager(this.silosManager);
    if (pdarsUpdater) pdarsUpdater.setPdarsManager(this.pdarsManager);
    if (aircraftUpdater)
      aircraftUpdater.setAircraftManager(this.aircraftManager);
    if (primitivesUpdater)
      primitivesUpdater.setPrimitivesManager(this.primitivesManager);
    if (heatmapUpdater) heatmapUpdater.setHeatmapManager(this.heatmapManager);
    if (pdarProbabilityFieldUpdater)
      pdarProbabilityFieldUpdater.setManager(this.pdarProbabilityFieldManager);

    this.interactionManager = new InteractionManager(this.map, this.eventBus);

    this.eventBus.emit("map:ready", this.map);
  }

  getEventBus(): MapEventBus {
    return this.eventBus;
  }
  getInteractionManager(): InteractionManager | null {
    return this.interactionManager;
  }
  getAircraftManager(): AircraftManager | null {
    return this.aircraftManager;
  }
  // 🌟 ADDED: Unified access channel for hooks to query system arrays
  getSiloManager(): SilosManager | null {
    return this.silosManager;
  }
  getHeatmapManager(): HeatmapManager | null {
    return this.heatmapManager;
  }
  getPdarProbabilityFieldManager(): PdarProbabilityFieldManager | null {
    return this.pdarProbabilityFieldManager;
  }
  getPdarsManager(): PdarsManager | null {
    return this.pdarsManager;
  }
  getMap(): MapType | null {
    return this.map;
  }

  // 🌟 FIX 1: Removed 'await' since updateAircraftBatch returns void synchronously
  updateAircraft(target: SkopaTarget) {
    this.aircraftManager?.updateAircraftBatch([target]);
  }

  // 🌟 FIX 2: Swapped to updateSilosBatch and removed await to match the actual manager API
  updateSilos(silo: SkopaPlatform) {
    this.silosManager?.updateSilosBatch([silo]);
  }

  selectAircraft(track_id: string) {
    this.aircraftManager?.selectAircraft(track_id);
  }
  deselectAircraft(track_id: string) {
    this.aircraftManager?.deselectAircraft(track_id);
  }

  // 🌟 ADDED: Direct target control loops matching Aircraft interaction paradigm
  selectSilo(stationId: string) {
    this.silosManager?.selectSilo?.(stationId);
  }
  deselectSilo(stationId: string) {
    this.silosManager?.deselectSilo?.(stationId);
  }

  remove() {
    if (this.updaterRegistry) this.updaterRegistry.destroy();
    if (this.map) this.map.remove();
    this.map = null;
    this.updaterRegistry = null;
    this.eventBus.clear();
  }
}
